import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../../api/apiClient';
import InfoBox from '../InfoBox';
import AuditTable, { formatTs, monoShort, pre, statusPill } from '../AuditTable';

export default function ProvenanceTab({ onOpenTrace }) {
  const { t } = useTranslation();
  const [label, setLabel] = useState('');
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await apiService.searchAuditLineage({
        label: label || undefined,
        q: q || undefined,
        limit: 50,
      });
      setItems(resp?.items || []);
    } catch (_) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [label, q]);

  useEffect(() => { search(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openLineage = async (row) => {
    setSelected({ loading: true, row });
    try {
      const data = await apiService.getAuditLineage(row.label, row.merge_value);
      setSelected({ data, row });
    } catch (e) {
      setSelected({ error: e?.response?.data?.detail || 'error', row });
    }
  };

  const columns = [
    { key: 'label', header: t('traceability:columns.label') },
    { key: 'merge_key', header: t('traceability:columns.mergeKey') },
    { key: 'merge_value', header: t('traceability:columns.mergeValue') },
    { key: 'n_mutations', header: t('traceability:provenance.col_mutations') },
    { key: 'first_seen', header: t('traceability:provenance.col_firstSeen'),
      render: (r) => formatTs(r.first_seen) },
    { key: 'last_seen', header: t('traceability:provenance.col_lastSeen'),
      render: (r) => formatTs(r.last_seen) },
  ];

  return (
    <div>
      <InfoBox tabKey="provenance" />

      <div className="flex flex-wrap gap-3 items-end mb-4 pb-4 border-b border-border-subtle">
        <label className="flex flex-col gap-1">
          <span className="text-label tracking-widest text-ink-muted uppercase">
            {t('traceability:provenance.filterLabel')}
          </span>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value.replace(/[^a-zA-Z0-9_:]/g, ''))}
            placeholder="Person, MassGrave, Document…"
            maxLength={64}
            className="bg-surface border border-border-strong rounded px-2 py-1 text-sm text-ink w-56"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-label tracking-widest text-ink-muted uppercase">
            {t('traceability:provenance.filterSearch')}
          </span>
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('traceability:provenance.searchPlaceholder')}
            maxLength={200}
            className="bg-surface border border-border-strong rounded px-2 py-1 text-sm text-ink w-72"
          />
        </label>
        <button
          onClick={search}
          className="text-label tracking-widest px-4 py-2 border border-accent/40 bg-accent/10 text-accent-text rounded hover:bg-accent/20">
          {t('traceability:filters.apply')}
        </button>
        <button
          onClick={() => { setLabel(''); setQ(''); setTimeout(search, 0); }}
          className="text-label tracking-widest text-ink-muted hover:text-accent-text px-3 py-1">
          {t('traceability:filters.clear')}
        </button>
      </div>

      <AuditTable
        rows={items}
        columns={columns}
        loading={loading}
        onRowClick={openLineage}
      />

      {selected && (
        <LineageDrawer
          state={selected}
          onClose={() => setSelected(null)}
          onOpenTrace={onOpenTrace}
        />
      )}
    </div>
  );
}

function LineageDrawer({ state, onClose, onOpenTrace }) {
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <aside className="relative w-full max-w-3xl h-full bg-surface border-l border-border-subtle overflow-auto">
        <header className="sticky top-0 bg-surface border-b border-border-subtle px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-label tracking-widest text-ink-muted uppercase">
              {t('traceability:provenance.drawerTitle')}
            </div>
            <code className="text-sm text-ink">
              {state.row?.label} · {state.row?.merge_key}={state.row?.merge_value}
            </code>
          </div>
          <button type="button" onClick={onClose}
                  className="text-ink-secondary hover:text-ink px-2" aria-label="Close">✕</button>
        </header>

        <div className="p-6">
          {state.loading && <div className="text-ink-muted">···</div>}
          {state.error && (
            <div className="px-4 py-2 text-sm rounded border bg-red-50 text-red-800 border-red-300 dark:bg-red-950/60 dark:text-red-200 dark:border-red-800">
              {t('traceability:provenance.error')}: {state.error}
            </div>
          )}
          {state.data && <LineageContent data={state.data} onOpenTrace={onOpenTrace} />}
        </div>
      </aside>
    </div>
  );
}

function LineageContent({ data, onOpenTrace }) {
  const { t } = useTranslation();
  return (
    <>
      <section className="mb-6">
        <div className="text-label tracking-widest text-ink-muted uppercase mb-2">
          {t('traceability:provenance.summary')}
        </div>
        <dl className="grid grid-cols-2 gap-y-1 text-sm">
          <dt className="text-ink-muted">{t('traceability:columns.label')}</dt>
          <dd className="text-ink-secondary">{data.node?.label}</dd>
          <dt className="text-ink-muted">{t('traceability:columns.mergeKey')}</dt>
          <dd className="text-ink-secondary">{data.node?.merge_key}</dd>
          <dt className="text-ink-muted">{t('traceability:columns.mergeValue')}</dt>
          <dd className="text-ink-secondary font-mono">{data.node?.merge_value}</dd>
          <dt className="text-ink-muted">{t('traceability:provenance.col_firstSeen')}</dt>
          <dd className="text-ink-secondary">{formatTs(data.first_seen)}</dd>
          <dt className="text-ink-muted">{t('traceability:provenance.col_lastSeen')}</dt>
          <dd className="text-ink-secondary">{formatTs(data.last_seen)}</dd>
          <dt className="text-ink-muted">{t('traceability:provenance.col_mutations')}</dt>
          <dd className="text-ink-secondary">{data.total_mutations}</dd>
        </dl>
      </section>

      <section>
        <div className="text-label tracking-widest text-ink-muted uppercase mb-3">
          {t('traceability:provenance.chain')} ({data.mutations?.length || 0})
        </div>
        <ol className="space-y-4">
          {(data.mutations || []).map((m, i) => (
            <MutationCard key={m.mutation?.event_id || i} entry={m} onOpenTrace={onOpenTrace} />
          ))}
        </ol>
      </section>
    </>
  );
}

function MutationCard({ entry, onOpenTrace }) {
  const { t } = useTranslation();
  const m = entry.mutation || {};
  const run = entry.ingestion_run;
  const doc = (entry.documents || [])[0];
  const transformLLM = (entry.llm_calls || []).find((l) => l.phase === 'transform_rows')
                    || (entry.llm_calls || [])[0];
  const cypher = (entry.cypher_executions || [])[0];
  const writeBatch = (entry.ingestion_events || []).find((e) => e.phase === 'write_batch');

  return (
    <li className="border border-border-subtle bg-surface-raised/40 rounded-lg p-4 text-xs text-ink-secondary">
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className="text-label tracking-widest text-ink-muted">{formatTs(m.timestamp)}</span>
          <span className="text-accent-text font-medium">{m.op || 'MERGE'}</span>
          {statusPill(m.status)}
        </div>
        {entry.trace_id && (
          <button
            onClick={() => onOpenTrace?.(entry.trace_id)}
            className="text-label tracking-widest text-ink-muted hover:text-accent-text">
            trace: {monoShort(entry.trace_id, 10)}
          </button>
        )}
      </header>

      {run && (
        <Row label={t('traceability:provenance.run')}>
          <div>
            run_id: <code className="text-ink">{monoShort(run.run_id, 12)}</code>
            {run.source && <> · source: <code className="text-ink">{run.source}</code></>}
            {run.status && <> · <span className="text-ink-muted">status={run.status}</span></>}
          </div>
        </Row>
      )}

      {doc && (
        <Row label={t('traceability:provenance.sourceFile')}>
          <div>
            <code className="text-ink">{doc.source}</code>
            {doc.checksum && (
              <div className="text-ink-muted mt-0.5">
                sha256: <code>{String(doc.checksum).slice(0, 24)}…</code>
              </div>
            )}
            {doc.mapping_key && (
              <div className="text-ink-muted mt-0.5">mapping: {doc.mapping_key}</div>
            )}
          </div>
        </Row>
      )}

      {writeBatch && (
        <Row label={t('traceability:provenance.phase')}>
          <div>
            {writeBatch.phase} · batch rows_in={writeBatch.rows_in ?? '—'} → rows_out={writeBatch.rows_out ?? '—'}
            {writeBatch.duration_ms != null && <> · {writeBatch.duration_ms} ms</>}
          </div>
        </Row>
      )}

      {transformLLM && (
        <Row label={t('traceability:provenance.llmCall')}>
          <div>
            <div>model: <code className="text-ink">{transformLLM.model}</code>
              · phase: {transformLLM.phase}
              {transformLLM.tokens_in != null && <> · tokens in={transformLLM.tokens_in}</>}
              {transformLLM.tokens_out != null && <> out={transformLLM.tokens_out}</>}
              {transformLLM.latency_ms != null && <> · {transformLLM.latency_ms} ms</>}
            </div>
            {transformLLM.prompt_preview && (
              <details className="mt-1">
                <summary className="cursor-pointer text-ink-muted">prompt_preview</summary>
                {pre(transformLLM.prompt_preview, 800)}
              </details>
            )}
            {transformLLM.response_preview && (
              <details className="mt-1">
                <summary className="cursor-pointer text-ink-muted">response_preview</summary>
                {pre(transformLLM.response_preview, 800)}
              </details>
            )}
            {transformLLM.prompt_hash && (
              <div className="text-ink-muted mt-1">
                prompt_hash: <code>{String(transformLLM.prompt_hash).slice(0, 24)}…</code>
              </div>
            )}
          </div>
        </Row>
      )}

      {cypher && (
        <Row label={t('traceability:provenance.cypher')}>
          {pre(cypher.cypher, 500)}
        </Row>
      )}

      <div className="mt-3 pt-2 border-t border-border-subtle text-ink-muted">
        event_id: {monoShort(m.event_id, 16)} · chain_hash: {monoShort(m.chain_hash, 16)}
        {m.props_hash && <> · props_hash: {monoShort(m.props_hash, 16)}</>}
      </div>
    </li>
  );
}

function Row({ label, children }) {
  return (
    <div className="mb-2">
      <div className="text-label tracking-widest text-ink-muted uppercase mb-0.5">{label}</div>
      <div className="text-ink-secondary">{children}</div>
    </div>
  );
}
